"use client";

import { useState } from "react";
import { generateCoverLetter, AiToolError } from "@/lib/platform/aiTools";
import type { CoverLetterDraft } from "@/lib/platform/aiTools";

export default function IntakeModal({
  onGenerated,
  onClose,
}: {
  onGenerated: (info: { jobTitle: string; company: string; fullName: string; email: string }, draft: CoverLetterDraft) => void;
  onClose: () => void;
}) {
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [keyStrengths, setKeyStrengths] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!jobTitle.trim() || !company.trim() || !keyStrengths.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const draft = await generateCoverLetter({
        jobTitle: jobTitle.trim(),
        company: company.trim(),
        keyStrengths: keyStrengths.trim(),
      });
      onGenerated({ jobTitle: jobTitle.trim(), company: company.trim(), fullName: fullName.trim(), email: email.trim() }, draft);
    } catch (e) {
      setError(e instanceof AiToolError ? e.message : "Couldn't generate a draft — please try again.");
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-[#0f172a]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
        <h2 className="pr-8 text-xl font-bold text-slate-900 dark:text-white">Let&apos;s draft your cover letter</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          We&apos;ll generate a first draft — you can edit every word afterwards.
        </p>

        {error ? <p className="mt-3 text-sm text-rose-500">{error}</p> : null}

        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Job Title</label>
            <input
              required
              autoFocus
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Senior Product Manager"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Company</label>
            <input
              required
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Corp"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Key strengths / highlights</label>
            <textarea
              required
              rows={3}
              value={keyStrengths}
              onChange={(e) => setKeyStrengths(e.target.value)}
              placeholder="5 years in B2B SaaS, led a team of 6, shipped a feature that grew revenue 20%…"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Full Name</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-110 disabled:opacity-60"
          >
            {loading ? "Drafting…" : "Generate Draft →"}
          </button>
        </form>
      </div>
    </div>
  );
}
